import {
    colorForAgentId,
    configNameFromMetadata,
    stripThreadNameTimestamp
} from '@/lib/deepIntelligenceAvatar';
import { cn } from '@/lib/utils';
import { CheckCheck, Library, Loader2, Plus } from 'lucide-react';
import {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ChainlitContext, IThread } from '@chainlit/react-client';

import {
    BriefCard,
    DisplayBrief
} from '@/components/ScheduledAgents/BriefCard';
import { FeedJumpNav } from '@/components/ScheduledAgents/FeedJumpNav';
import {
    JobFilterControl,
    JobFilterOption
} from '@/components/ScheduledAgents/JobFilterControl';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import {
    TIMELINE_OPTIONS,
    TimelineFilterControl,
    TimelineOption
} from '@/components/ScheduledAgents/TimelineFilterControl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { useDeepIntelligenceThreads } from '@/hooks/DMuseDeepIntelligenceThreads';
import { useDeepIntelligenceUnreadCount } from '@/hooks/DMuseDeepIntelligenceUnreadCount';
import { useAuth } from '@/hooks/useAuth';
import { useIsBasicUser } from '@/hooks/useIsBasicUser';

import { BriefKPI } from './data';

function colorForId(id: string): string {
    return colorForAgentId(id);
}

function formatTimeAgo(createdAt: string | number): string {
    const diffMs = Date.now() - new Date(createdAt).getTime();
    const minutes = Math.max(0, Math.round(diffMs / (1000 * 60)));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d ago`;
    return `${Math.round(days / 7)}w ago`;
}

function liveToDisplay(
    thread: IThread,
    currentUserId: string | undefined
): DisplayBrief {
    // Group/filter by the specific scheduled job (config_id), not the
    // underlying managed agent (managed_agent_id) — multiple differently-
    // named jobs can share one managed agent, and used to collapse into a
    // single filter pill (and lose their own color) when keyed by agent id.
    const agentId =
        thread.metadata?.config_id ??
        thread.metadata?.managed_agent_id ??
        'deep-intelligence';
    const configName = configNameFromMetadata(thread.metadata);
    return {
        id: thread.id,
        agentId,
        // The small badge next to the avatar shows which configuration
        // produced this run, and the large headline shows the run's own
        // (now user-editable) thread name. The avatar letter also represents
        // the configuration.
        agentName: configName,
        avatarLabel: configName,
        agentColor: colorForId(agentId),
        timeAgo: formatTimeAgo(thread.createdAt),
        createdAtMs: new Date(thread.createdAt).getTime(),
        headline: thread.name
            ? stripThreadNameTimestamp(thread.name)
            : 'Untitled run',
        summary:
            thread.metadata?.summary ??
            'Generated by a scheduled Deep Intelligence job.',
        kpis: Array.isArray(thread.metadata?.kpis)
            ? (thread.metadata.kpis as BriefKPI[])
            : [],
        // Each configuration connects to exactly one managed agent today
        // (single-select), so the count is 1 whenever that link exists.
        sources: thread.metadata?.managed_agent_id ? '1' : '—',
        isOwner: !!currentUserId && thread.userId === currentUserId,
        isUnread: thread.isRead === false
    };
}

const DAY_MS = 24 * 60 * 60 * 1000;

function dayGroupLabel(createdAtMs: number): string {
    const startOfToday = new Date().setHours(0, 0, 0, 0);
    if (createdAtMs >= startOfToday) return 'Today';
    if (createdAtMs >= startOfToday - DAY_MS) return 'Yesterday';
    if (createdAtMs >= startOfToday - 7 * DAY_MS) return 'Previous 7 days';
    if (createdAtMs >= startOfToday - 30 * DAY_MS) return 'Previous 30 days';
    return new Date(createdAtMs).toLocaleString('default', {
        month: 'long',
        year: 'numeric'
    });
}

function timeAgoToHours(timeAgo: string): number {
    if (timeAgo === 'Just now') return 0;
    const match = timeAgo.match(/^(\d+)([mhdw])/);
    if (!match) return Infinity;
    const multipliers: Record<string, number> = {
        m: 1 / 60,
        h: 1,
        d: 24,
        w: 168
    };
    return parseInt(match[1]) * (multipliers[match[2]] ?? 1);
}

export default function StreamAnalysis() {
    const navigate = useNavigate();
    const isBasicUser = useIsBasicUser();
    const { agentId } = useParams<{ agentId: string }>();
    const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>(
        agentId ? [agentId] : []
    );
    const [timelineId, setTimelineId] = useState<TimelineOption['id']>('all');
    const scrollRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

    const [feedScrolled, setFeedScrolled] = useState(false);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => setFeedScrolled(el.scrollTop > 0);
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    const {
        threads: liveThreads,
        isLoading: liveLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        refresh
    } = useDeepIntelligenceThreads();
    const { user } = useAuth();
    const apiClient = useContext(ChainlitContext);
    const {
        unreadCount,
        markAllRead,
        refresh: refreshUnreadCount
    } = useDeepIntelligenceUnreadCount();

    useEffect(() => {
        refreshUnreadCount();
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleDelete = useCallback(async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await apiClient.deleteThread(deleteId);
            refresh();
            setDeleteId(null);
        } catch (err) {
            toast.error('Failed to delete analysis');
        } finally {
            setDeleting(false);
        }
    }, [deleteId, apiClient, refresh]);

    const allBriefs: DisplayBrief[] = useMemo(
        () => liveThreads.map((t) => liveToDisplay(t, user?.id)),
        [liveThreads, user?.id]
    );

    const timelineOption =
        TIMELINE_OPTIONS.find((o) => o.id === timelineId) ??
        TIMELINE_OPTIONS[TIMELINE_OPTIONS.length - 1];
    const cutoffMs =
        timelineOption.hours == null
            ? null
            : Date.now() - timelineOption.hours * 60 * 60 * 1000;

    const oldestLoadedMs =
        allBriefs.length > 0
            ? Math.min(...allBriefs.map((b) => b.createdAtMs))
            : null;

    const moreWithinWindow =
        hasMore &&
        (cutoffMs === null ||
            oldestLoadedMs === null ||
            oldestLoadedMs > cutoffMs);

    const hasCutoff = cutoffMs !== null;

    useEffect(() => {
        if (!hasCutoff || liveLoading || isLoadingMore) return;
        if (!moreWithinWindow) return;
        loadMore();
    }, [hasCutoff, liveLoading, isLoadingMore, moreWithinWindow, loadMore]);

    useEffect(() => {
        const container = scrollRef.current;
        const sentinel = loadMoreSentinelRef.current;
        if (!container || !sentinel || !moreWithinWindow) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMore();
            },
            { root: container, rootMargin: '200px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [moreWithinWindow, loadMore]);

    const jobFilterOptions: JobFilterOption[] = useMemo(
        () =>
            Array.from(
                new Map(
                    allBriefs.map((b) => [
                        b.agentId,
                        {
                            id: b.agentId,
                            name: b.agentName,
                            color: b.agentColor
                        }
                    ])
                ).values()
            ),
        [allBriefs]
    );

    const briefs = (
        selectedAgentIds.length > 0
            ? allBriefs.filter((b) => selectedAgentIds.includes(b.agentId))
            : allBriefs
    )
        .filter((b) => cutoffMs === null || b.createdAtMs >= cutoffMs)
        .toSorted(
            (a, b) => timeAgoToHours(a.timeAgo) - timeAgoToHours(b.timeAgo)
        );

    const [activeId, setActiveId] = useState(briefs[0]?.id ?? '');

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="top-0 z-10 sticky bg-background dark:bg-background/80 shrink-0">
                <MobileNavTrigger />
                <div className="flex flex-wrap justify-between items-start gap-4 mx-auto px-4 sm:px-12 pt-8 sm:pt-12 pb-8 max-w-agentPageWidth">
                    <div className="flex flex-col gap-1 min-w-0">
                        <h1 className="font-medium text-foreground text-2xl lg:text-3xl">
                            My Feed
                        </h1>
                        <p className="text-(length:--fs-title) text-muted-foreground">
                            Discover and subscribe to scheduled jobs published
                            across your organization
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button
                            className="text-sm"
                            variant="tertiary"
                            size="default"
                            onClick={() => navigate('/scheduled-jobs/bank')}
                        >
                            <Library />
                            Jobs bank
                        </Button>
                        {!isBasicUser && (
                            <Button
                                className="text-sm"
                                variant="primary"
                                size="default"
                                onClick={() => navigate('/scheduled-jobs/new')}
                            >
                                <Plus />
                                Create new job
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Job + timeline filters */}
            <div className="shrink-0">
                <div
                    className={cn(
                        'flex flex-wrap justify-between items-center mx-auto px-4 sm:px-12 py-6 border-b max-w-agentPageWidth transition-colors',
                        feedScrolled ? 'border-border' : 'border-transparent'
                    )}
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <JobFilterControl
                            options={jobFilterOptions}
                            selectedIds={selectedAgentIds}
                            onChange={(ids) => {
                                setSelectedAgentIds(ids);
                                const first =
                                    ids.length > 0
                                        ? allBriefs.find((b) =>
                                              ids.includes(b.agentId)
                                          )
                                        : allBriefs[0];
                                setActiveId(first?.id ?? '');
                            }}
                        />
                        <TimelineFilterControl
                            value={timelineId}
                            onChange={setTimelineId}
                        />
                    </div>
                    {unreadCount > 0 && (
                        <Button
                            className="text-sm"
                            variant="tertiary"
                            size="default"
                            onClick={() => {
                                markAllRead();
                                refresh();
                            }}
                        >
                            {/* <CheckCheck className="size-4!" /> */}
                            Mark all read
                        </Button>
                    )}
                </div>
            </div>

            <div className="flex flex-1 mx-auto px-4 sm:px-12 max-w-agentPageWidth overflow-hidden">
                {/* Spacer that mirrors the mx-auto left margin of max-w-agentPageWidth */}
                <div className="hidden lg:block w-[max(0px,calc((100%-1032px)/2))] shrink-0" />
                {/* Left jump-nav */}
                {briefs.length > 0 && (
                    <FeedJumpNav
                        briefs={briefs}
                        activeId={activeId}
                        onActiveIdChange={setActiveId}
                        scrollContainerRef={scrollRef}
                    />
                )}

                {/* Right scroll column — extends to page edge, scrollbar at far right */}
                <div
                    ref={scrollRef}
                    className="flex-1 dark:bg-background lg:ml-12 overflow-y-auto overscroll-contain"
                >
                    <div className="flex flex-col gap-5 px-6 pt-2 pb-[130px] max-w-[780px]">
                        {briefs.map((brief, index) => {
                            const label = dayGroupLabel(brief.createdAtMs);
                            const startsGroup =
                                index === 0 ||
                                dayGroupLabel(briefs[index - 1].createdAtMs) !==
                                    label;
                            return (
                                <Fragment key={brief.id}>
                                    {startsGroup && (
                                        <div
                                            role="separator"
                                            className="flex items-center gap-3 pt-2 text-muted-foreground text-sm"
                                        >
                                            <span className="flex-1 bg-border h-px" />
                                            <span>{label}</span>
                                            <span className="flex-1 bg-border h-px" />
                                        </div>
                                    )}
                                    <BriefCard
                                        brief={brief}
                                        articleHref={`/scheduled-jobs/article/${brief.id}`}
                                        onManage={
                                            brief.isOwner
                                                ? () =>
                                                      navigate(
                                                          `/scheduled-jobs/manage/${brief.agentId}`
                                                      )
                                                : undefined
                                        }
                                        onDelete={
                                            brief.isOwner
                                                ? () => setDeleteId(brief.id)
                                                : undefined
                                        }
                                    />
                                </Fragment>
                            );
                        })}
                        {liveLoading ? (
                            <div
                                className="flex flex-col gap-5"
                                aria-busy="true"
                                aria-label="Loading your scheduled job runs"
                            >
                                {Array.from({ length: 3 }, (_, index) => (
                                    <Skeleton
                                        key={index}
                                        className="rounded-xl h-64"
                                    />
                                ))}
                            </div>
                        ) : moreWithinWindow ? (
                            <div
                                ref={loadMoreSentinelRef}
                                className="flex justify-center items-center gap-2 py-6 text-muted-foreground text-sm text-center"
                            >
                                {isLoadingMore && (
                                    <>
                                        <Loader2 className="size-3.5 animate-spin" />
                                        Loading more…
                                    </>
                                )}
                            </div>
                        ) : (
                            <p className="py-6 text-muted-foreground text-sm text-center">
                                You're all caught up - runs from your scheduled
                                jobs will appear here
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <AlertDialog
                open={!!deleteId}
                onOpenChange={(open) => !open && setDeleteId(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this job?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes it from your feed - and from the feed
                            of everyone else who subscribes to this job. This
                            action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={deleting}
                            className="px-4 py-4 text-base"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={handleDelete} disabled={deleting} className="px-4 py-4 text-base">
                                {deleting ? 'Deleting…' : 'Delete'}
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
